import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronRight, Loader2 } from "lucide-react";
import { searchHistoricalItems } from "../../services/apiService";
import {
  SCOPES,
  SCOPE_HINTS,
  actualHoursPerUnit,
  classifyCushion,
  countWithActuals,
  cushionEvidenceFlags,
  cushionLabel,
  formatCushion,
  formatHours,
  hasActuals,
  quotedHoursPerUnit,
  cushionRatio,
  sharedAcrossLines,
} from "./costedJobReference";
import "./CostedJobReferences.css";

const PAGE_SIZE = 50;

/**
 * Costed Job References — evidence, in place.
 *
 * This is a section INSIDE the rate-card form, sitting with the pull-rates
 * strip right where department labor hours get typed: the moment a past job's
 * numbers actually inform the judgement. It never takes the centre pane over;
 * the card being built stays on screen above and below it.
 *
 * One row per indexed costing-sheet line: what the sheet quoted for labor and,
 * where Island Time has a match, what the shop actually clocked. The table
 * stays lean because the column is narrow — picking a row opens the whole
 * story in the inspector, which is also where "Pull rates" lives.
 *
 * Search runs through the same `searchHistoricalItems` wiring the historical
 * dialog uses; nothing new was added to the API surface for this screen.
 */
function CostedJobReferences({
  scope,
  onScopeChange,
  selectedId,
  onSelect,
  onResults,
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // Every fetch takes a ticket, but searches and "show more" queue SEPARATELY:
  // one shared counter let a page-2 fetch take the newest number and thereby
  // silence the newer search behind it — the search's own response was dropped
  // as stale and its `finally` skipped, wedging the table on "Searching…"
  // forever. Two counters plus a query check means a response is written only
  // when it is the newest of its own kind AND it answers the query on screen.
  const searchSeq = useRef(0);
  const moreSeq = useRef(0);
  // The query the table is currently meant to be showing, readable the instant
  // it changes rather than one render later.
  const activeQuery = useRef(query.trim());
  // Mirror of `results` that is current the instant it is set, so loadMore
  // reads the real offset and appends to the real list rather than to whatever
  // the render that created its closure happened to see.
  const resultsRef = useRef([]);

  const publish = useCallback(
    (items) => {
      resultsRef.current = items;
      if (typeof onResults === "function") onResults(items);
    },
    [onResults],
  );

  // One debounced search per keystroke run, empty query included — an empty
  // box means "show me everything indexed", the same as the dialog.
  useEffect(() => {
    let cancelled = false;
    const q = query.trim();
    // Claimed at keystroke time, not at fetch time: an in-flight "show more"
    // for the previous query is disowned the moment the box changes.
    activeQuery.current = q;
    const timer = setTimeout(async () => {
      const seq = (searchSeq.current += 1);
      const isCurrent = () =>
        !cancelled && seq === searchSeq.current && q === activeQuery.current;
      setLoading(true);
      setError(null);
      try {
        const data = await searchHistoricalItems({
          q,
          limit: PAGE_SIZE,
          offset: 0,
        });
        if (!isCurrent()) return;
        const items = data?.results || [];
        setResults(items);
        setTotal(data?.total || 0);
        publish(items);
      } catch (err) {
        if (!isCurrent()) return;
        setError(err?.error || err?.message || "Search failed");
        setResults([]);
        setTotal(0);
        publish([]);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, publish]);

  const loadMore = async () => {
    // Paging is only meaningful against a settled result set. With a search in
    // flight the offset on screen belongs to the OLD query, so page 2 would be
    // the new query's rows taken from the old query's offset — a page of the
    // right search stitched on at the wrong place. The button is disabled
    // while loading; this guards the programmatic path too.
    if (loading || loadingMore) return;
    const q = query.trim();
    const seq = (moreSeq.current += 1);
    const isCurrent = () =>
      seq === moreSeq.current && q === activeQuery.current;
    setLoadingMore(true);
    try {
      const data = await searchHistoricalItems({
        q,
        limit: PAGE_SIZE,
        offset: resultsRef.current.length,
      });
      if (!isCurrent()) return;
      const page = data?.results || [];
      const merged = [...resultsRef.current, ...page];
      setResults(() => merged);
      // The corpus can grow (or an item be deleted) between pages; trust the
      // total the page we just read reported over the one from page 1.
      if (Number.isFinite(Number(data?.total))) setTotal(Number(data.total));
      publish(merged);
    } catch (err) {
      if (!isCurrent()) return;
      setError(err?.error || err?.message || "Failed to load more lines");
    } finally {
      if (seq === moreSeq.current) setLoadingMore(false);
    }
  };

  const missingActuals = results.length - countWithActuals(results);

  return (
    <div className="cjr" data-scope={scope}>
      <div className="cjr__toolbar">
        <div className="cjr__searchwrap">
          <label className="cjr__lbl" htmlFor="cjr-search">
            Search costed lines
          </label>
          <input
            id="cjr-search"
            className="cjr__input"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Name or job number — bracket, IM6202…"
          />
        </div>
        <div>
          <span className="cjr__lbl">Labor shown</span>
          <div
            className="cjr__seg"
            role="group"
            aria-label="Which labor numbers to show"
          >
            {SCOPES.map((option) => (
              <button
                key={option.value}
                type="button"
                className="wb-plain"
                aria-pressed={scope === option.value}
                onClick={() => onScopeChange(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* The with-actuals count lives in the section header, where it is the
          scent that makes this worth expanding. Here: what is on screen now,
          and what the scope switcher is doing. */}
      <div className="cjr__meta">
        <span>
          <strong>{results.length}</strong> costed line
          {results.length === 1 ? "" : "s"} shown
          {total > results.length ? ` of ${total}` : ""}
        </span>
        <span>{SCOPE_HINTS[scope]}</span>
      </div>

      <div className="cjr__scroll">
        <table className="cjr__table">
          <thead>
            <tr>
              <th className="cjr__c-name">Line item</th>
              <th>Job</th>
              <th>Material</th>
              <th className="cjr__c-num">Qty</th>
              <th className="cjr__c-num cjr__col-est">Quoted hr/unit</th>
              <th className="cjr__c-num cjr__col-act">Actual hr/unit</th>
              <th className="cjr__c-num cjr__col-cush">Cushion</th>
              <th className="cjr__c-open">
                <span className="sr-only">Open</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {results.map((item) => (
              <ReferenceRow
                key={item.id}
                item={item}
                selected={item.id === selectedId}
                onSelect={onSelect}
              />
            ))}
            {results.length === 0 && (
              <tr>
                <td className="cjr__state" colSpan={8}>
                  {loading ? (
                    <span>
                      <Loader2 className="inline h-3.5 w-3.5 animate-spin" />{" "}
                      Searching the indexed sheets…
                    </span>
                  ) : error ? (
                    error
                  ) : (
                    "No costed lines match that search."
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {results.length > 0 && total > results.length && (
        <div className="mt-3 flex justify-center">
          <button
            type="button"
            className="wb-tbtn wb-tbtn--edge"
            onClick={loadMore}
            disabled={loading || loadingMore}
          >
            {loadingMore
              ? "Loading…"
              : `Show more (${total - results.length} left)`}
          </button>
        </div>
      )}

      {/* A claim about "every line shown" is only true when lines are shown:
          under an empty or failed search it would be asserting coverage over
          nothing. */}
      {results.length > 0 && (
        <p className="cjr__sub cjr__footnote">
          {missingActuals > 0
            ? `${missingActuals} of these ${results.length} lines have no Island Time match yet — still usable, you are just pulling rates from the quote alone.`
            : "Every line shown has clocked shop hours behind it."}
        </p>
      )}
    </div>
  );
}

/** One sentence per caveat, so a row carrying both says both. */
function evidenceTitle(flag, item) {
  return flag === "unverified-qty"
    ? "The unit count behind these shop hours was guessed, not read off the job — the per-unit figure, and this ratio, are approximate."
    : `One job-level actual was written onto ${sharedAcrossLines(item)} line items — this is not a per-line measurement.`;
}

function ReferenceRow({ item, selected, onSelect }) {
  const quoted = quotedHoursPerUnit(item);
  const actual = actualHoursPerUnit(item);
  const ratio = cushionRatio(item);
  const tone = classifyCushion(ratio);
  const evidenceFlags = cushionEvidenceFlags(item);

  return (
    <tr
      className={selected ? "is-on" : undefined}
      onClick={() => onSelect(item)}
    >
      <td className="cjr__c-name">
        <span className="cjr__nm">{item.name}</span>
        {(item.description || item.dimensions_raw) && (
          <span className="cjr__dims">
            {item.description || item.dimensions_raw}
          </span>
        )}
      </td>
      <td>{item.job_number || "—"}</td>
      <td>
        {item.material_type ? (
          <span className="cjr__mat">{item.material_type}</span>
        ) : (
          <span className="cjr__na-tag">Unknown</span>
        )}
      </td>
      <td className="cjr__c-num">{item.quantity ?? "—"}</td>
      <td className="cjr__c-num cjr__col-est">
        <span className="cjr__hr-q">{formatHours(quoted)}</span>
      </td>
      <td className="cjr__c-num cjr__col-act">
        {hasActuals(item) ? (
          <span className="cjr__hr-a">{formatHours(actual)}</span>
        ) : (
          <span className="cjr__na-tag">No shop hours logged</span>
        )}
      </td>
      <td className="cjr__c-num cjr__col-cush">
        {tone ? (
          /* Colour is a verdict, and a verdict needs a measured number behind
             it. A cushion computed off a guessed quantity or a job-level total
             shared across lines gets the neutral chip and says so. */
          evidenceFlags.length === 0 ? (
            <span className={`cjr__chip cjr__chip--${tone}`}>
              {cushionLabel(ratio)}
            </span>
          ) : (
            /* The short label can only carry one caveat — the guessed quantity
               wins, because it moves the number itself. The tooltip carries
               every caveat that applies, so nothing is silently dropped. */
            <span
              className="cjr__chip cjr__chip--soft"
              title={evidenceFlags
                .map((flag) => evidenceTitle(flag, item))
                .join(" ")}
            >
              {evidenceFlags[0] === "unverified-qty"
                ? `~${formatCushion(ratio)} · unverified qty`
                : `~${formatCushion(ratio)} · job-level`}
            </span>
          )
        ) : (
          <span className="cjr__chip cjr__chip--none">
            {hasActuals(item) ? "No quoted hours" : "No actuals"}
          </span>
        )}
      </td>
      <td className="cjr__c-open">
        <button
          type="button"
          className="wb-plain cjr__openbtn"
          aria-label={`Open ${item.name} detail`}
          onClick={(e) => {
            e.stopPropagation();
            onSelect(item);
          }}
        >
          <ChevronRight aria-hidden="true" />
        </button>
      </td>
    </tr>
  );
}

export default CostedJobReferences;
